/**
 * <TutorApplicationChampionTemplate> — une page de la candidature où un champion parle, seul en scène
 * (template).
 *
 * La règle « un champion qui parle se montre en pied, avec sa bulle » (`CLAUDE.md`), pour une page
 * entière : l'intro d'une étape et sa fin. Le champion en pied dans son halo, ce qu'il dit dans sa bulle
 * (`<SpeechBubble>`, la phrase qui entre quand `stepKey` change) : au téléphone, la bulle au-dessus de
 * lui, la queue vers le bas, et l'action au pied de la page ; au grand écran, côte à côte, la queue vers
 * lui, l'action sous la bulle.
 *
 * Un seul arbre dans les deux cas : le passage du seuil ne change que la direction et la queue, le
 * champion (et sa danse, tirée une fois) n'est jamais remonté.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { ChampionFigure, type ChampionPose } from "../../atoms/ChampionFigure"
import { Stack } from "../../atoms/containers/Stack"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { SpeechBubble } from "../../molecules/SpeechBubble"
import { PageTemplate } from "../PageTemplate"

/** Le champion et sa bulle, côte à côte au large. */
const SCENE_WIDTH = 880
const SIDE_BY_SIDE = 640

export type TutorApplicationChampionTemplateProps = {
    champion: ChampionName
    /** Défaut `main`. */
    pose?: ChampionPose
    /** Sa hauteur au téléphone, puis au grand écran. */
    figureHeight: { narrow: number; wide: number }
    /** Ce qui précède la scène (« Étape terminée ! »). */
    above?: React.ReactNode
    /** Ce qu'il dit. */
    text: string
    /** La phrase change : elle entre dans la bulle, qui reste. */
    stepKey?: string
    /** Sous la bulle (où l'on en est des phrases) ; reçoit la disposition. */
    below?: (wide: boolean) => React.ReactNode
    /** « Continuer » : au pied de la page au téléphone, sous la bulle au grand écran. */
    action: React.ReactNode
    testID?: string
}

export function TutorApplicationChampionTemplate(props: TutorApplicationChampionTemplateProps) {
    const { champion, pose, figureHeight, above, text, stepKey, below, action, testID } = props
    const { onLayout, atLeast: wide } = useWidthAtLeast(SIDE_BY_SIDE)

    return (
        <PageTemplate testID={testID} padding="5" maxContentWidth={SCENE_WIDTH} footer={wide ? undefined : action}>
            <View style={styles.center} onLayout={onLayout}>
                <Stack gap="6">
                    {above}
                    <View style={styles.scene(wide)}>
                        <ChampionFigure
                            name={champion}
                            pose={pose}
                            height={wide ? figureHeight.wide : figureHeight.narrow}
                            halo
                        />
                        <Stack gap="6" style={styles.side(wide)}>
                            <Stack gap="3">
                                <SpeechBubble tail={wide ? "left" : "down"} size="lg" stepKey={stepKey}>
                                    {text}
                                </SpeechBubble>
                                {below?.(wide)}
                            </Stack>
                            {wide && <View style={styles.wideAction}>{action}</View>}
                        </Stack>
                    </View>
                </Stack>
            </View>
        </PageTemplate>
    )
}

const styles = StyleSheet.create((theme) => ({
    center: { flexGrow: 1, justifyContent: "center", paddingVertical: theme.spacing["6"] },
    // Au téléphone, la bulle (second enfant) passe au-dessus du champion.
    scene: (wide: boolean) => ({
        flexDirection: wide ? "row" : "column-reverse",
        alignItems: "center",
        justifyContent: "center",
        gap: wide ? theme.spacing["6"] : theme.spacing["2"],
    }),
    side: (wide: boolean) => ({
        alignSelf: wide ? ("auto" as const) : ("stretch" as const),
        flex: wide ? 1 : undefined,
        minWidth: 0,
        maxWidth: wide ? 440 : undefined,
    }),
    wideAction: { maxWidth: 320 },
}))
